import type { PlacarDaRodada } from "../../game/types";

/**
 * A partida como a página inteira a desenha.
 *
 * O solo monta isto a partir da `LiveView` do motor; o online, a partir do
 * `match.start` e dos `match.event` que o servidor transmite. A página não
 * sabe de onde veio: as duas escalações, os lances com a jogada e a posse por
 * trecho são o que o campo precisa, e é o que os dois modos dão.
 */

export type EspecieDoLance =
  | "gol"
  | "chance"
  | "defesa"
  | "amarelo"
  | "vermelho"
  | "lesao"
  | "substituicao"
  | "penalti_perdido"
  | "posse"
  | "falta"
  | "impedimento"
  | "outro";

export type Jogada = "armacao" | "drible" | "cruzamento" | "contra_ataque" | "bola_parada" | "penalti";

export const JOGADAS: readonly Jogada[] = ["armacao", "drible", "cruzamento", "contra_ataque", "bola_parada", "penalti"];

export interface JogadorDaPartida {
  id: string;
  name: string;
  shirt: number | null;
  pos: string;
  nationality?: string;
}

/** Uma vaga da formação: quem está nela e se já foi expulso. */
export interface VagaDaPartida {
  jogador: JogadorDaPartida;
  sentOff: boolean;
}

export interface LadoDaPartida {
  clubId: string;
  name: string;
  short: string;
  colors: [string, string];
  formation: string;
  slots: string[];
  custom: Array<{ pos: string; x: number; y: number }> | null;
  /** Quem está em cada vaga, na ordem das vagas. */
  emCampo: VagaDaPartida[];
}

export interface LanceDaPartida {
  /** Ordem de chegada. Dois lances podem cair no mesmo minuto. */
  ordem: number;
  minute: number;
  kind: EspecieDoLance;
  clubId: string | null;
  playerId: string | null;
  assistId: string | null;
  shooterId: string | null;
  play: Jogada | null;
  key: string;
  params: Record<string, string | number>;
}

export interface PartidaView {
  competition: string;
  stage: string;
  home: LadoDaPartida;
  away: LadoDaPartida;
  homeGoals: number;
  awayGoals: number;
  minute: number;
  finished: boolean;
  /** Nulo para quem assiste sem clube na partida. */
  myClubId: string | null;
  lances: LanceDaPartida[];
  /** Os outros jogos da rodada. Vazio quando o modo não os tem. */
  placares: PlacarDaRodada[];
}

/** A espécie do lance a partir do que o motor ou o protocolo dizem. */
export function especieDoLance(kind: string, key: string): EspecieDoLance {
  if (key === "lance.posse") return "posse";
  switch (kind) {
    case "gol":
    case "goal":
      return "gol";
    case "chance":
      return key === "lance.defesa" ? "defesa" : "chance";
    case "defesa":
      return "defesa";
    case "amarelo":
      return "amarelo";
    case "vermelho":
      return "vermelho";
    case "card":
      return key === "lance.amarelo" ? "amarelo" : "vermelho";
    case "lesao":
    case "injury":
      return "lesao";
    case "substituicao":
    case "sub":
      return "substituicao";
    case "penalti_perdido":
    case "penalty":
      return "penalti_perdido";
    case "falta":
    case "foul":
      return "falta";
    case "impedimento":
    case "offside":
      return "impedimento";
    default:
      return "outro";
  }
}

export function jogadaDe(play: string | null | undefined): Jogada | null {
  return play && (JOGADAS as readonly string[]).includes(play) ? (play as Jogada) : null;
}
